// Angular
import {
	Component,
	ComponentFactoryResolver,
	EventEmitter,
	OnInit,
	OnDestroy,
	ViewChild,
} from '@angular/core';
// State
import { Store } from '@ngxs/store';
import { SaveFilterForm, SelectedRowOrder } from './state/action/inbox.action';
// Service
import { OrderInboxService } from './service/order-inbox.service';
import {
	BroadcastEventModel,
	BroadcastService,
	SubContentService,
	ToasterType,
	SetIsHasPermissions
} from '@cv-invision/core';
import { CvDateFormatter } from '@cv-invision/cv.component-library.ux';
// Component
import { CustomActionCellComponent } from './custom-action-cell/custom-action-cell.component';
import { OrderContentDetailComponent } from './order-content-detail/order-content-detail.component';
import { CustomFlagsCellComponent } from './custom-flags-cell/custom-flags-cell.component';
import { CustomIDCellComponent } from './custom-id-cell/custom-id-cell.component';
import { CustomDateCellComponent } from '@cv-invision/core';
import { CustomStatusCellComponent } from './custom-status-cell/custom-status-cell.component';
import { CustomTooltipFlagCellComponent } from './custom-tooltip-cell/custom-tooltip-cell.component';
import { CustomSmartqcCellComponent } from './custom-smartqc-cell/custom-smartqc-cell.component';
// Model
import { OrderInboxColumnsModel } from './model/order-inbox-columns.model';
import { FORMFILTERLIST, ORDERSTATES, DEFAULTORDERSTATE, FILTERSSROPTIONS } from './model/state-status.model';
import { PaginationModel } from '@cv-invision/cv.component-library.ux';
import { InboxStateModel } from './state/model/inbox-state.model';
// Others
import { from, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil, tap } from 'rxjs/operators';
import { orderBy } from 'lodash';

export const RELOAD_INBOX = 'reloadInboxEvent';

@Component({
	selector: 'cv-inbox',
	templateUrl: './inbox.component.html',
	styleUrls: ['./inbox.component.scss']
})
export class InboxComponent implements OnInit, OnDestroy {
	@ViewChild('inboxGrid', { static: true }) agGrid: any;
	// State
	inboxStateModel$: Observable<InboxStateModel> = this.store.select((state: any) => state.intelligence.inbox) ;

	public defaultOrderStatesValues: Array<any>;
	public filterSSR: Array<any>;
	public formList: Array<any>;
	public gridOptions: any;
	// Inbox Filter Form Model
	public inboxFilterForm: any;
	public orderStates: Array<any>;
	public orderList: any;
	public profileList: Array<any>;
	public selectedOrderRow: any;

	private atComponentDestroy = new Subject();
	private formTypeValues: Array<any>;
	private paginationChangeFunctionId: any;
	private statusValues: Array<any>;

	// Event emitter to grab bounced values for the Address
	private addressChanged: EventEmitter<string> = new EventEmitter<string>();
	private clientNameChanged: EventEmitter<string> = new EventEmitter<string>();
	private referenceIDChanged: EventEmitter<string> = new EventEmitter<string>();

	constructor(
		private orderInboxService: OrderInboxService,
		private store: Store,
		private subContentService: SubContentService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private broadcastService: BroadcastService,
		private dateService:CvDateFormatter
	) {
		this.orderStates = ORDERSTATES;
		this.defaultOrderStatesValues = DEFAULTORDERSTATE;
		this.filterSSR = FILTERSSROPTIONS;
		this.formList = FORMFILTERLIST;
		this.inboxFilterForm = {
			address: '',
			clientName: '',
			referenceID: '',
			// Inbox start by default using 3 possible status
			orderStates: this.defaultOrderStatesValues.map(x => Object.assign({}, x)),
			startDate: null,
			endDate: null,
			profileSelected: null,
			pagination: null,
			inboxGlobalFilter: null,
			formType: []
		};
		this.profileList = [];
		this.statusValues = [];
		this.formTypeValues = [];
		this.inboxStateModel$
			.pipe(
				take(1),
				tap((inboxStateModel: InboxStateModel) => {
					if (inboxStateModel && inboxStateModel.inboxFilterForm) {
						this.inboxFilterForm.address = inboxStateModel.inboxFilterForm.address;
						this.inboxFilterForm.referenceID = inboxStateModel.inboxFilterForm.referenceID;
						this.inboxFilterForm.ssrFilterOrders = inboxStateModel.inboxFilterForm.ssrFilterOrders;
						this.inboxFilterForm.orderStates = inboxStateModel.inboxFilterForm.orderStates.map(x =>
							Object.assign({}, x)
						);
						this.inboxFilterForm.formType = inboxStateModel.inboxFilterForm.formType.map(x =>
							Object.assign([], x)
						);
						this.inboxFilterForm.profileSelected = {
							...inboxStateModel.inboxFilterForm.profileSelected
						};
						this.inboxFilterForm.startDate = this.setDate(inboxStateModel.inboxFilterForm.startDate, 'startDate');
						this.inboxFilterForm.endDate = this.setDate(inboxStateModel.inboxFilterForm.endDate, 'endDate');
						this.inboxFilterForm.pagination = Object.assign(
							{},
							inboxStateModel.inboxFilterForm.pagination
						);
						this.inboxFilterForm.inboxGlobalFilter = {
							...inboxStateModel.inboxFilterForm.inboxGlobalFilter
						};
						this.statusValues = this.inboxFilterForm.orderStates;
						this.formTypeValues = this.inboxFilterForm.formType;
					} else {
						this.restorePagination();
					}
					if (inboxStateModel && inboxStateModel.selectedOrderRow) {
						this.selectedOrderRow = inboxStateModel.selectedOrderRow;
					}
				})
			)
			.subscribe();
		this.registerInboxListener();
	}

	ngOnInit(): void {
		this.openSubComponent();

		if (this.inboxFilterForm.startDate === null) {
			this.inboxFilterForm.startDate = this.defaultInitDate();
		}

		this.gridOptions = {
			rowHeight: 40,
			rowSelection: 'single',
			defaultColDef: {
				resizable: true,
				tooltipComponent: 'customTooltipFlagCellComponent'
			},
			localeText: {
				noRowsToShow: 'No orders to show',
				loadingOoo: 'Loading orders...'
			},
			columnDefs: new OrderInboxColumnsModel().columns,
			frameworkComponents: {
				customFlagsCellComponent: CustomFlagsCellComponent,
				customIDCellComponent: CustomIDCellComponent,
				customActionCellComponent: CustomActionCellComponent,
				customDateCellComponent: CustomDateCellComponent,
				customStatusCellComponent: CustomStatusCellComponent,
				customTooltipFlagCellComponent: CustomTooltipFlagCellComponent,
				customSmartqcCellComponent: CustomSmartqcCellComponent,
			}
		};
		this.onQueryInbox();
		this.getAllProfiles();

		// Debounce to avoid to spam the server for the Inputs
		this.addressChanged
			.pipe(
				debounceTime(500),
				distinctUntilChanged()
			)
			.subscribe(model => {
				this.inboxFilterForm.address = model || '';
				this.onQueryInbox();
			});

		this.clientNameChanged
			.pipe(
				debounceTime(500),
				distinctUntilChanged()
			)
			.subscribe(model => {
				this.inboxFilterForm.clientName = model || '';
				this.onQueryInbox();
			});

		this.referenceIDChanged
			.pipe(
				debounceTime(500),
				distinctUntilChanged()
			)
			.subscribe(model => {
				this.inboxFilterForm.referenceID = model || '';
				this.onQueryInbox();
			});

		this.orderInboxService.currentHeaderInbox
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((result: any) => {
				if (result && result.reloadInbox) {
					this.reloadInbox();
				} else if (result && result.applyGlobalFilters) {
					this.applyGlobalFilters(result);
				}
			});

		// Subscribe for changes on the state of selectedOrderRow
		this.inboxStateModel$.pipe(takeUntil(this.atComponentDestroy)).subscribe(inboxStateData => {
			if (inboxStateData.selectedOrderRow) {
				this.selectedOrderRow = inboxStateData.selectedOrderRow;
			} else {
				this.selectedOrderRow = 0;
			}
		});

		this.store
			.select(s => s.user.requestPermissions)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(requestPermissions => {
				if (requestPermissions) {
					// the router.navigate shallow the toaster interceptor msj
					this.broadcastService.broadcast({
						name: ToasterType.ERROR,
						event: {
							error: requestPermissions.message
						}
					});
					this.store.dispatch(new SetIsHasPermissions(null));
				}
			});
	}

	/**
	 * set default date
	 * @returns
	 */
	private defaultInitDate(): Date {
		// Inbox Filter default start date value
		const startDate = new Date();

		startDate.setMonth(startDate.getMonth() - 3);

		return startDate;
	}

	/**
	 * Register Lister to Show/Hide the Loader
	 */
	private registerInboxListener(): void {
		this.broadcastService.on(RELOAD_INBOX, (broadcast: BroadcastEventModel) => {
			this.onQueryInbox();
		});
	}

	/**
	 * Applies a global filter search for the inbox list. This is called by the inbox-header.component
	 * @param result: any
	 */
	private applyGlobalFilters(result: any): void {
		this.inboxFilterForm.inboxGlobalFilter = { ...result.applyGlobalFilters };
		if (this.inboxFilterForm.inboxGlobalFilter && this.inboxFilterForm.inboxGlobalFilter.filters) {
			this.inboxFilterForm.orderStates = [];
		} else {
			this.inboxFilterForm.orderStates = this.statusValues.length
				? this.statusValues
				: [...this.defaultOrderStatesValues];

			this.inboxFilterForm.formType = this.formTypeValues.length
				? this.formTypeValues
				: [];
		}
		this.onQueryInbox();

		// Clear selected row order when a global filter is applied
		this.store.dispatch(new SelectedRowOrder(0));
		this.openSubComponent();
	}

	/**
	 * cc the values of the states on value change
	 * @param event
	 */
	public onOrderStateValueChange(event: any): void {
		this.restorePagination();
		this.inboxFilterForm.orderStates = event.map(x => Object.assign({}, x));
		this.statusValues = [...this.inboxFilterForm.orderStates];
		this.onQueryInbox();
	}

	/**
	 * cc the values of the profile selected on value change
	 * @param event
	 */
	public onProfilesValueChange(event: any): void {
		this.restorePagination();
		this.inboxFilterForm.profileSelected = Object.assign({}, event);
		this.onQueryInbox();
	}

	/**
	 * Address value has change
	 */
	public onAddressValueChange(event: any): void {
		this.restorePagination();
		this.addressChanged.emit(event.value);
	}

	/**
	 * Client Name value has change
	 */
	public onClientNameValueChange(event: any): void {
		this.restorePagination();
		this.clientNameChanged.emit(event.value);
	}

	/**
	 * Address value has change
	 */
	public onReferenceIDValueChange(event: any): void {
		this.restorePagination();
		this.referenceIDChanged.emit(event.value);
	}

	/**
	 * Emit the change when the Start Date or End Date is selected
	 */
	public onDateSelect(): void {
		this.restorePagination();
		this.onQueryInbox();
	}

	/**
	 * set SSR value filter
	 */
	public filterSsrRules(event: any): void {
		this.restorePagination();
		this.inboxFilterForm.ssrFilterOrders = Object.assign({}, event);
		this.onQueryInbox();
	}

	/**
	 * set Form value filter
	 */
	public filterForm (event: any): void {
		this.restorePagination();
		this.inboxFilterForm.formType = event.map(x => Object.assign({}, x));
		this.formTypeValues = [...this.inboxFilterForm.formType];
		this.onQueryInbox();
	}

	/**
	 * Execute the Query to get the Inbox Orders based on the Filters
	 */
	private onQueryInbox(): void {
		this.store.dispatch(
			new SaveFilterForm({
				address: this.inboxFilterForm.address,
				referenceID: this.inboxFilterForm.referenceID,
				orderStates: this.inboxFilterForm.orderStates.map(x => Object.assign({}, x)),
				profileSelected: Object.assign({}, this.inboxFilterForm.profileSelected),
				endDate: this.inboxFilterForm.endDate,
				startDate: this.inboxFilterForm.startDate,
				ssrFilterOrders: Object.assign({}, this.inboxFilterForm.ssrFilterOrders),
				pagination: Object.assign({}, this.inboxFilterForm.pagination),
				inboxGlobalFilter: Object.assign({}, this.inboxFilterForm.inboxGlobalFilter),
				formType: this.inboxFilterForm.formType,
				clientName: this.inboxFilterForm.clientName
			})
		);

		this.orderInboxService.getReviewerInbox(this.inboxFilterForm).subscribe(
			(orderList: any) => {
				// if current page from pagination selected exceeds from the total items, then return to first page.
				if (orderList.offset > orderList.totalCount) {
					this.inboxFilterForm.pagination.offset = 0;
					this.onQueryInbox();
				} else {
					this.orderList = orderList.data;
					this.inboxFilterForm.pagination.limit = orderList.limit;
					this.inboxFilterForm.pagination.offset = orderList.offset;
					this.inboxFilterForm.pagination.totalCount = orderList.totalCount;
				}
			},
			error => {
				this.orderList = from([]);
			}
		);
	}

	/**
	 * Get All Profiles
	 */
	private getAllProfiles(): void {
		this.orderInboxService.getProfiles().subscribe((profileList: any) => {
			this.profileList = orderBy(profileList, ['name']);
		});
	}

	/**
	 * When the grid has just started, we make the content to fit
	 */
	public onInitializeGrid(params: any): void {
		this.agGrid = params.api;
		this.resizeToFitColumns();
	}

	/**
	 * Grid has new Data to show
	 */
	public onRowDataChanged(): void {
		if (this.selectedOrderRow) {
			this.agGrid.forEachNode(node =>
				node.data.id === this.selectedOrderRow ? node.setSelected(true) : 0
			);
		}
	}

	/**
	 * Every time the view port has change, we ensure the columns fits properly
	 */
	public resizeToFitColumns(): void {
		this.agGrid.sizeColumnsToFit();
	}

	/**
	 * Selected order
	 */
	public onSelectionChanged(): void {
		const selectedRows = this.agGrid.getSelectedRows();
		const selectedRow = selectedRows[0];
		// console.log(selectedRow);
		// Save Last Selected Row
		this.store.dispatch(new SelectedRowOrder(selectedRow ? selectedRow.id : 0));
		this.openSubComponent(selectedRow);
	}

	/**
	 * Open the component with the Selected Row
	 * @param selectedRow
	 */
	private openSubComponent(selectedRow: any = null): void {
		// Open Order Detail
		this.subContentService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: OrderContentDetailComponent,
				data: selectedRow
			})
			.subscribe();
	}

	/**
	 * Get the new instance of the Pagination and Execute the new Query
	 * @param paginationModel
	 */
	public onPaginationChange(paginationModel: PaginationModel): void {
		clearTimeout(this.paginationChangeFunctionId);

		this.paginationChangeFunctionId = setTimeout(() => {
			this.inboxFilterForm.pagination = paginationModel;
			this.onQueryInbox();
		}, 250);
	}

	/**
	 * Restore pagination to initial
	 */
	private restorePagination(): void {
		this.inboxFilterForm.pagination = {
			totalCount: 0,
			offset: 0,
			limit: 10
		};
	}

	/**
 * check type of startDate or endDate from inboxStateModel$ and set startDate or endDate
 * @param date
 */
	private setDate(date: any, dateType: string): Date {
		const checkType = typeof date;
		const isDateInstance = date instanceof Date;

		if (checkType === 'string') {
			return new Date(date);
		} else {
			if (date === null) {
				return dateType === 'startDate' ? this.defaultInitDate() : null;
			} else if (date !== null && isDateInstance) {
				return date;
			} else {
				return new Date(date.year, date.month - 1, date.day);
			}
		}
	}

	/**
	 * On refresh button click, reload the grid information based on the current filters, pagination, etc.
	 */
	reloadInbox(): void {
		this.onQueryInbox();
	}

	/**
	 * Close all when the element is destroyed
	 * in case a Sub Component remains visible
	 */
	ngOnDestroy(): void {
		this.subContentService.close();
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
